import { Currency } from "@redotech/money/currencies";
import { checkoutOptimizationBillingSchema } from "@redotech/redo-billing-common/checkout-optimization-billing-schema";
import { InsertionPosition } from "@redotech/redo-storefront-common/insertion-position";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { FulfillmentCutoffScheduleParser } from "./fulfillment-cutoff";

/**
 * Merchant-app Conversion Cloud surfaces that can be independently locked via
 * admin-managed `checkoutOptimization.lockedFeatures`. These are **frontend
 * presentation hooks only** (sidebar hide/lock-icon + merchant-app route
 * redirects) — they do not disable storefront serving, billing, or live
 * widgets. Dashboard and Welcome are intentionally not members.
 */
export enum ConversionFeature {
  AB_TESTS = "AB_TESTS",
  ANALYTICS_CHECKOUT = "ANALYTICS_CHECKOUT",
  ANALYTICS_POST_PURCHASE = "ANALYTICS_POST_PURCHASE",
  ANALYTICS_CUSTOM_REPORTS = "ANALYTICS_CUSTOM_REPORTS",
  CHECKOUTS = "CHECKOUTS",
  DISCOUNT_RULES = "DISCOUNT_RULES",
  POST_PURCHASE = "POST_PURCHASE",
  PRICING_TESTS = "PRICING_TESTS",
  SPOTLIGHTS = "SPOTLIGHTS",
  WIDGETS = "WIDGETS",
}

export const CONVERSION_FEATURES = Object.values(
  ConversionFeature,
) as ConversionFeature[];

/**
 * Canonical PPU / self-serve intent lock list: every surface except Spotlights.
 * Written by activateIntentRescue and by manual migration of intent-only teams.
 */
export const DEFAULT_INTENT_LOCKED_FEATURES: readonly ConversionFeature[] =
  CONVERSION_FEATURES.filter((f) => f !== ConversionFeature.SPOTLIGHTS);

const postPurchaseUpsellSchema = z.object({
  createOrderOnUpsell: z.boolean().optional(),
});

const progressBarSchema_ = z.object({
  threshold: z.object({
    amount: z.number(),
    currency: createEnum(Currency, { enumName: "Currency" }),
  }),
  ltThresholdText: z.string(),
  gteThresholdText: z.string(),
  progressBarColor: z.string(),
  selectors: z.array(
    z.object({
      selector: z.string(),
      insertPosition: createEnum(InsertionPosition, {
        enumName: "InsertionPosition",
      }),
    }),
  ),
});
export const progressBarSchema: z.ZodType<ProgressBar> = progressBarSchema_;
export interface ProgressBar extends z.infer<typeof progressBarSchema_> {}

const checkoutOptimizationSettingsSchema = z.object({
  enabled: z.boolean(),
  infinityTestingEnabled: z.boolean().optional(),
  preventSplitShipmentsRateAddition: z.boolean().optional(),
  postPurchaseUpsell: postPurchaseUpsellSchema.optional(),
  fulfillmentCutoffSchedule: FulfillmentCutoffScheduleParser.optional(),
  checkoutOptimizationBilling: checkoutOptimizationBillingSchema.optional(),
  ensureDiscountsCombineWithShippingDiscounts: z.boolean().optional(),
  subscriptionsModeEnabled: z.boolean().optional(),
  /**
   * When enabled (and subscriptions mode is on), the rates endpoint skips the
   * invisible rate-name uniqueness character for the first occurrence of each
   * rate name, so rate names stay clean for consumers that bypass the delivery
   * customization function. See `stripCheckoutOptimizationInvisibleIndicators`.
   */
  hideInvisibleRateNameCharsWhenPossible: z.boolean().optional(),
  storefrontProgressBar: progressBarSchema_.optional(),
  useShippingOptionMetafields: z.boolean().optional(),
  isUsingDiscountRules: z.boolean().optional(),
  syncDeliveryProfileProductMetafields: z.boolean().optional(),
  /**
   * Frontend presentation hooks for the merchant Conversion app (sidebar +
   * in-app routes only — not storefront). Empty = nothing locked. Populated
   * for partial tiers / after intent-only merchants migrate to
   * checkoutOptimization.enabled + a lock list.
   */
  lockedFeatures: z
    .array(createEnum(ConversionFeature, { enumName: "ConversionFeature" }))
    .optional()
    .default([]),
});

export interface CheckoutOptimizationSettings extends z.infer<
  typeof checkoutOptimizationSettingsSchema
> {}

export const checkoutOptimizationSettingsDefault: CheckoutOptimizationSettings =
  {
    enabled: false,
    infinityTestingEnabled: false,
    preventSplitShipmentsRateAddition: false,
    ensureDiscountsCombineWithShippingDiscounts: false,
    lockedFeatures: [],
  };

const CheckoutOptimizationAnalyticsAssumptionsZodSchema = z.object({
  annualOrders: z.number().optional(),
});
export interface CheckoutOptimizationAnalyticsAssumptions extends z.infer<
  typeof CheckoutOptimizationAnalyticsAssumptionsZodSchema
> {}

export interface CheckoutOptimizationBillingCreator {
  /**
   * Distinguishes this billing creator type from any other string to
   * ensure the creator conforms to the correct string format.
   */
  __checkoutOptimizationBillingCreator: true;
  value: string;
}

export function getBillingSettingsCreator({
  creatorEmail,
}: {
  creatorEmail: string;
}): CheckoutOptimizationBillingCreator {
  return {
    __checkoutOptimizationBillingCreator: true,
    value: `admin:${creatorEmail}`,
  };
}

export function getSystemBillingSettingsCreator(
  label: string,
): CheckoutOptimizationBillingCreator {
  return {
    __checkoutOptimizationBillingCreator: true,
    value: `system:${label}`,
  };
}
